import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useItemDialogFocus from "../useItemDialogFocus";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import "./ItemClassificationPicker.css";
import axios from "../../../api/axiosInstance";
import DeliveryNoteLocationPicker from "./DeliveryNoteLocationPicker";
import "./ReceivingDestinations.css";
import { resolveRequestBusinessUnitId } from "../../../currency/currencyStorage";

let nextLineKey = 1;
export const createDestinationLineKey = () => `receiving-line-${nextLineKey++}`;


export function useDestinationSaveQueue() {
  const queues = useRef(new Map());
  const enqueue = (key, save) => {
    const pending = (queues.current.get(key) || Promise.resolve()).catch(() => {}).then(save);
    queues.current.set(key, pending);
    const cleanup = () => { if (queues.current.get(key) === pending) queues.current.delete(key); };
    pending.then(cleanup, cleanup);
    return pending;
  };
  enqueue.flush = () => Promise.all([...queues.current.values()]);
  return enqueue;
}

const itemId = (line) => Number(line.item_id || line.manual_item_id || line.suggested_item_id || line.item?.id || 0);
const identity = (line, index) => String(line._clientKey || line._destinationKey || line.temp_id || line.__key || line.id || `new-${index}`);

// An automatic destination is a preview, not a manual choice. Keep its ID out
// of the line payload so reopening the document retains the distinction.
export function deliveryNoteLineLocation(line, headerLocationId, suggestion, { posted = false, pendingPoLines = [] } = {}) {
  if (posted) return line.posted_destination_location_id || line.destination_location_id || headerLocationId || null;
  if (line.destination_location_id || headerLocationId) return line.destination_location_id || headerLocationId;
  const receipts = line.po_receipts || [];
  const inherited = receipts.map((receipt) => {
    const po = pendingPoLines.find((entry) => Number(entry.id) === Number(receipt.po_line_id));
    return receipt.destination_location_id || receipt.po_line_destination_location_id || receipt.po_destination_location_id || po?.destination_location_id || po?.order?.destination_location_id;
  }).filter(Boolean).map(Number);
  const unique = [...new Set(inherited)];
  if (unique.length > 1) return null;
  return unique[0] || line.receipt_destination_location_id || line.po_line_destination_location_id || line.po_destination_location_id || suggestion?.destination_location_id || null;
}

// Shared by PR, PO, receiving and direct/OCR delivery notes. A response may only
// fill a still-empty line for the same item: manual edits always win races.
export function useReceivingDestinations({ lines = [], headerLocationId, onChange, disabled = false, scopeKey = "", pendingPoLines = [], requireAssignedDestination = false }) {
  const businessUnitId = resolveRequestBusinessUnitId({});
  const [suggestions, setSuggestions] = useState({});
  const [error, setError] = useState(false);
  const latest = useRef({ lines, onChange, disabled, headerLocationId, pendingPoLines });
  latest.current = { lines, onChange, disabled, headerLocationId, pendingPoLines };
  const idsKey = [...new Set(lines.map(itemId).filter(Boolean))].sort((a, b) => a - b).join(",");
  const lineKey = lines.map((line, index) => `${identity(line, index)}:${itemId(line)}:${(line.po_receipts || []).map((receipt) => receipt.po_line_id).join(",")}`).join("|");
  const poKey = pendingPoLines.map((line) => `${line.id}:${line.destination_location_id || line.order?.destination_location_id || ""}`).join("|");

  const previousHeader = useRef({ value: headerLocationId, scopeKey, businessUnitId });
  useEffect(() => {
    const previous = previousHeader.current;
    previousHeader.current = { value: headerLocationId, scopeKey, businessUnitId };
    if (disabled || previous.scopeKey !== scopeKey || previous.businessUnitId !== businessUnitId || String(previous.value || "") === String(headerLocationId || "")) return;
    // A deliberate header edit replaces automatic/inherited choices in this
    // editing session. An explicit line selection always remains authoritative.
    latest.current.lines.forEach((line, index) => {
      if (itemId(line) && (["suggested", "inherited", "header"].includes(line.destination_location_source) || !line.destination_location_id)) {
        latest.current.onChange(index, Number(headerLocationId) || null, headerLocationId ? "header" : "suggested");
      }
    });
  }, [headerLocationId, scopeKey, businessUnitId, disabled]);

  useEffect(() => {
    if (!idsKey || disabled) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const requested = lines.map((line, index) => ({ key: identity(line, index), item: itemId(line) }));
    const ids = idsKey.split(",").map(Number);
    setError(false);
    if (requireAssignedDestination) setSuggestions({});
    const load = async () => {
      const results = [];
      for (let index = 0; index < ids.length; index += 500) {
        const response = await axios.post("/api/purchasing/inventory/locations/receiving-suggestions", { item_ids: ids.slice(index, index + 500), ...(requireAssignedDestination ? { require_assigned_destination: true } : {}) }, { signal: controller.signal });
        results.push(...(response.data?.suggestions || []));
      }
      if (cancelled || resolveRequestBusinessUnitId({}) !== businessUnitId) return;
      const byItem = Object.fromEntries(results.map((result) => [Number(result.item_id), result]));
      setSuggestions(byItem);
      if (requireAssignedDestination) return;
      for (const requestedLine of requested) {
        const current = latest.current;
        const index = current.lines.findIndex((line, i) => identity(line, i) === requestedLine.key && itemId(line) === requestedLine.item);
        const line = current.lines[index];
        if (current.disabled || !line) continue;
        // Opening a persisted document is read-only; defaults are frozen by its
        // first save. Users may still explicitly change the select below.
        if (line.id && !line.destination_location_source) continue;
        const linkedIds = new Set((line.po_receipts || []).map((receipt) => Number(receipt.po_line_id)));
        const linkedLocations = [...new Set(current.pendingPoLines.filter((poLine) => linkedIds.has(Number(poLine.id))).map((poLine) => Number(poLine.destination_location_id || poLine.order?.destination_location_id || 0)).filter(Boolean))];
        const linkedDestination = linkedLocations.length === 1 ? linkedLocations[0] : null;
        if (line.destination_location_id && !(line.destination_location_source === "suggested" && linkedDestination)) continue;
        const inherited = linkedDestination || line.po_line_destination_location_id || line.po_line?.destination_location_id || line.order?.destination_location_id || line.po_destination_location_id;
        const destination = inherited || current.headerLocationId || byItem[requestedLine.item]?.destination_location_id;
        if (destination) current.onChange(index, destination, inherited || current.headerLocationId ? "inherited" : "suggested");
      }
    };
    load().catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; controller.abort(); };
    // The signatures identify item/line changes without restarting on quantity edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, lineKey, poKey, scopeKey, businessUnitId, headerLocationId, disabled, requireAssignedDestination]);

  return { suggestions, error };
}

// Render this control inside the product cell; no separate per-item location panel.
export function useInlineDestinations({ lines = [], items = [], locations = [], headerLocationId, onChange, ...options }) {
  const { t } = useReceivingPopupUi();
  const { suggestions, error } = useReceivingDestinations({ lines, headerLocationId, onChange, ...options });
  return (line, index = lines.indexOf(line)) => {
    const id = itemId(line);
    if (!id || index < 0) return null;
    const item = items.find((entry) => Number(entry.id) === id);
    const name = line.item?.name || item?.name || line.item_name || line.description || `${t("Item")} #${id}`;
    return <>
      <DeliveryNoteLocationPicker compact
        value={line.destination_location_id || headerLocationId || ""}
        generalLocationId={headerLocationId}
        locations={suggestions[id]?.locations || locations}
        label={`${t("Destination Location")}: ${name}`}
        disabled={options.disabled || !suggestions[id]?.locations?.length}
        onChange={(destination, source) => onChange(index, destination, source)} />
      {error && <small className="receiving-location-error" role="status">{t("Could not load location suggestions. Saved locations are preserved.")}</small>}
    </>;
  };
}

const isMainLocation = (location) => Boolean(location.is_main) || String(location.code || "").trim().toUpperCase() === "MAIN";
export const permittedReceivingLocations = (locations = [], { includeHistorical = false } = {}) => {
  // Decide before applying permissions: losing access to the real warehouses
  // must not turn Main back into a receiving destination.
  const hasOtherLocations = locations.some((location) => location.is_active !== false && !isMainLocation(location));
  return locations.filter((location) => location.can_receive !== false && location.is_active !== false
    && (includeHistorical || (location.is_receiving_destination !== false && !(hasOtherLocations && isMainLocation(location)))));
};
const automaticReceivingLocation = (line, items = [], locations = []) => {
  const item = items.find((entry) => Number(entry.id) === itemId(line)) || line.item;
  const assigned = item && Object.prototype.hasOwnProperty.call(item, "automatic_destination_location_id")
    ? item.automatic_destination_location_id
    : item?.effective_destination_location_id || item?.destination_location_id || (!item && line.effective_destination_location_id);
  const allowed = permittedReceivingLocations(locations);
  if (assigned) return allowed.find((location) => Number(location.id) === Number(assigned))?.id || null;
  return allowed.length === 1 ? allowed[0].id : null;
};
export const receivingLineLocation = (line, headerLocationId, items = [], locations = [], { readOnly = false } = {}) => {
  const destination = line.destination_location_id || headerLocationId || automaticReceivingLocation(line, items, locations);
  if (readOnly) return destination;
  return permittedReceivingLocations(locations).find((location) => Number(location.id) === Number(destination))?.id || null;
};

export function ReceivingLocationPicker({ value, locations = [], label, onChange, disabled = false, allowInherited = false, compact = false, readOnly = false, requestOpen = 0 }) {
  const { t, themeId } = useReceivingPopupUi();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (requestOpen) { setQuery(""); setOpen(true); }
  }, [requestOpen]);
  const close = () => setOpen(false);
  const dialogRef = useItemDialogFocus(open && !disabled && !readOnly, false, close);
  const allowed = permittedReceivingLocations(locations);
  const displayedLocations = readOnly ? permittedReceivingLocations(locations, { includeHistorical: true }) : allowed;
  const selected = displayedLocations.find((location) => Number(location.id) === Number(value));
  const invalid = Boolean(value) && !selected;
  const filtered = allowed.filter((location) => `${location.name || ""} ${location.code || ""}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const choose = (id) => { if (!disabled && !readOnly) onChange(id); close(); };
  const destinationLabel = selected?.name || selected?.code || t(invalid ? "Location not authorized" : allowInherited ? "Use each item's destination" : "Select a receiving location");
  const triggerClass = `receiving-location-trigger ${compact ? "receiving-location-trigger--compact" : "form-control"}`;
  return <>
    {readOnly ? <span className={triggerClass} aria-label={label} data-invalid={invalid || undefined}>
      {compact ? <em>{destinationLabel}</em> : <span>{destinationLabel}</span>}
    </span> : <button type="button" className={triggerClass} aria-label={label} aria-haspopup="dialog" aria-expanded={open && !disabled}
      data-invalid={invalid || undefined} disabled={disabled || !allowed.length} onClick={() => { setQuery(""); setOpen(true); }}>
      {compact ? <em>{destinationLabel}</em> : <span>{destinationLabel}</span>}<span aria-hidden="true"> ▾</span>
    </button>}
    {invalid && <small role="alert">{t("The saved destination is not authorized. Select an available location.")}</small>}
    {open && !disabled && !readOnly && createPortal(<div className="pilcore-purchasing-ui icp-overlay" data-theme={themeId} onClick={(event) => { event.stopPropagation(); if (event.currentTarget === event.target) close(); }}>
      <section ref={dialogRef} className="icp-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
        <header><h3>{label}</h3><button type="button" className="icp-button" onClick={close}>{t("Close")}</button></header>
        <div className="icp-content">
          {requestOpen > 0 && <p role="status">{t("Select an authorized receiving location before saving quantities.")}</p>}
          <input type="search" value={query} aria-label={t("Search locations")} placeholder={t("Search locations")} onChange={(event) => setQuery(event.target.value)} />
          <div className="icp-cards">
            {allowInherited && <button type="button" className="icp-card" onClick={() => choose(null)}>{t("Use each item's destination")}</button>}
            {filtered.map((location) => <button type="button" className="icp-card" key={location.id} aria-pressed={Number(location.id) === Number(value)} onClick={() => choose(Number(location.id))}>
              <strong>{location.name || location.code}</strong>{location.code && <small>{location.code}</small>}
            </button>)}
          </div>{!filtered.length && <p role="status">{t("No matching locations.")}</p>}
        </div>
      </section>
    </div>, document.body)}
  </>;
}

export function useAuthorizedReceivingDestinations({ lines = [], items = [], locations = [], headerLocationId, onChange, disabled = false, scopeKey = "" }) {
  const allowed = permittedReceivingLocations(locations);
  const latest = useRef({ lines, items, allowed, onChange });
  latest.current = { lines, items, allowed, onChange };
  const previousHeader = useRef({ value: headerLocationId, scopeKey });
  const signature = lines.map((line, index) => `${identity(line, index)}:${itemId(line)}:${line.destination_location_id || ""}:${line.effective_destination_location_id || ""}:${line.destination_location_source || ""}`).join("|");
  const locationSignature = allowed.map((location) => location.id).join(",");
  useEffect(() => {
    const previous = previousHeader.current;
    previousHeader.current = { value: headerLocationId, scopeKey };
    if (disabled) return;
    const headerChanged = previous.scopeKey === scopeKey && String(previous.value || "") !== String(headerLocationId || "");
    const current = latest.current;
    current.lines.forEach((line, index) => {
      if (!itemId(line)) return;
      // Opening a saved receiving never moves existing stock. A deliberate
      // header change, however, replaces every per-line choice, even manual ones.
      if (!headerChanged && (line.destination_location_source || line.__receivingPersisted)) return;
      if (!headerChanged && line.destination_location_id && !current.allowed.some((location) => Number(location.id) === Number(line.destination_location_id))) return;
      const destination = headerLocationId || automaticReceivingLocation(line, current.items, current.allowed);
      if (destination && !current.allowed.some((location) => Number(location.id) === Number(destination))) {
        current.onChange(index, null, "inherited");
        return;
      }
      current.onChange(index, Number(destination) || null, headerLocationId ? "header" : "inherited");
    });
  }, [signature, locationSignature, headerLocationId, scopeKey, disabled]);
  return allowed;
}
